/**
 * Single source of truth for the human-readable label shown in the billing
 * "Type" column across every owner- and admin-facing billing surface.
 *
 * Precedence mirrors the admin RegistrationBillingForm exactly: `full` must win
 * over `single_payment`, because a paid-in-full plan can still carry the
 * registration-level `single_payment` flag (BillingScheduleResource derives that
 * flag independently of the per-row payment_type). SN-8310 — an `additional` row
 * (a rep's Add Invoice on a Team-Managed Budget share) wins over it too: it is one
 * extra charge, never the registration's one-time payment.
 */
export interface BillingTypeLabelRecord {
  payment_type?: string | null
  single_payment?: boolean | null
}

export const getBillingTypeLabel = (record: BillingTypeLabelRecord): string => {
  if (record.payment_type === 'complimentary') {
    return 'Complimentary'
  }
  if (record.payment_type === 'full') {
    return 'Full Payment'
  }
  if (record.payment_type === 'additional') {
    return 'Additional Invoice'
  }
  if (record.single_payment) {
    return 'One Time Payment'
  }
  if (record.payment_type === 'adhoc') {
    return 'AdHoc'
  }
  if (record.payment_type === 'deposit') {
    return 'Deposit'
  }
  return 'Installment'
}

export interface PayableRegistrationTypeRecord {
  registration_type?: string | null
  is_official_budget_line?: boolean | null
}

/**
 * Sep 24 — the "Type" a pending-payment card names. A team official's Team Budget
 * line is billed on a PLAYER-typed registration, so the wire says
 * `registration_type: 'Player'`; the card says "Team Rep" instead, the word the
 * Team Budget sheet types that row with. Everything else reads as sent.
 */
export const getPayableRegistrationTypeLabel = (
  record: PayableRegistrationTypeRecord
): string =>
  record.is_official_budget_line ? 'Team Rep' : record.registration_type ?? ''

export interface RegistrantDisplayNameRecord {
  is_official_budget_line?: boolean | null
  registrant_name?: string | null
  player?: { name_first?: string | null; name_last?: string | null } | null
}

/**
 * SN-8480 — whom a registration row is for. A Team Rep's own Team Budget line has no
 * player: the server names it by its display rule (`registrant_name`). A player row is
 * the player's name; anything else (a team) is nobody's, and shows the team alone.
 */
export const getRegistrantDisplayName = (
  record: RegistrantDisplayNameRecord
): string | null => {
  if (record.is_official_budget_line) {
    return record.registrant_name ?? null
  }
  if (record.player) {
    return `${record.player.name_first} ${record.player.name_last}`
  }
  return null
}

export interface OnlinePayableRecord {
  can_pay_online?: boolean | null
  paid?: boolean | null
  paid_in_person?: boolean | null
  refunded?: boolean | null
  subtotal?: number | string | null
  refund_subtotal?: number | string | null
  payment_provider_type?: { id?: string | number | null } | null
}

/**
 * SN-4709 Phase 7.1 (C12): the single client + mobile predicate for whether a billing
 * row can be charged through a payment provider. Every "pay online" affordance must
 * gate on this so a provider-less (offline) row never offers an online charge once the
 * server column goes nullable (Phase 7.1b).
 *
 * The server is authoritative — it now sends `can_pay_online` on every billing row
 * (BillingSchedule::canPayOnline). The legacy fallback (rows fetched before the field
 * existed) mirrors the old gate (`!paid && outstanding > 0`) PLUS a provider-presence
 * check. Works for both web (string provider id) and mobile (numeric provider id).
 */
export const canPayOnline = (record?: OnlinePayableRecord | null): boolean => {
  if (!record) {
    return false
  }
  if (typeof record.can_pay_online === 'boolean') {
    return record.can_pay_online
  }
  const outstanding =
    Number(record.subtotal ?? 0) - Number(record.refund_subtotal ?? 0) > 0
  const providerId = record.payment_provider_type?.id
  return (
    providerId != null &&
    providerId !== '' &&
    !record.paid &&
    !record.paid_in_person &&
    !record.refunded &&
    outstanding
  )
}

export interface ResumablePaymentRecord extends OnlinePayableRecord {
  can_resume_payment?: boolean | null
}

/**
 * Sep 24 — may /pay open this bill? When it is payable afresh (canPayOnline), or when the
 * server says its interrupted Stripe attempt can be resumed (`can_resume_payment`): a claimed
 * bill is never payable afresh, but the page may ask for its intent again, and the server
 * answers with the attempt's real state. A bill from an api2 that predates the field is never
 * resumable here — exactly today's gate.
 */
export const canPayOrResumeOnline = (
  record?: ResumablePaymentRecord | null
): boolean => canPayOnline(record) || record?.can_resume_payment === true
